import type { ClientFactsheet, QueueClient } from '../types/client';

/**
 * The adviser's own factsheet for this client — **ungated**.
 *
 * The factsheet document belongs to the ADVISER, not the client, and the server
 * has already scoped it: `clientController.getClient` assigns the root
 * `factsheetId` from `getClientFactsheetId(client, adviser)` (server
 * utils/client.js — the inactive client's own factsheet, otherwise the matching
 * `advisorDetails` entry's), and `formatClientData` attaches it **outside** the
 * `if (dataAccess)` block that withholds the client-owned fields. A client
 * revoking access does not take it away, and a later reconnection re-attaches
 * the same document (`shareController` keeps `inAdviserDetails.factsheetId` and
 * only creates a new factsheet when there is none), so the adviser's notes,
 * tags, families, tier and DISC survive a disconnection intact.
 *
 * This mirrors V1, which reads `get(client, 'factsheetId', {})` with no
 * connection test at all (`ClientEntry.js:603`) — name, nickname, importance,
 * isRecruitment, existingClient and dob all come off it for a 已中斷 client too.
 *
 * Use this for anything the ADVISER owns. Use `qualifyingFactsheetOf` only
 * where V1 itself gates on the connection — see below.
 */
export const adviserFactsheetOf = (client: QueueClient, adviserEmail?: string): ClientFactsheet | undefined => {
  if (client.factsheetId) {
    return client.factsheetId;
  }
  const details = client.advisorDetails || [];
  const detail = details.find(entry => !adviserEmail || entry.email === adviserEmail);
  // Note: on live data `advisorDetails[].factsheetId` is a bare ObjectId, never
  // a populated document — this fallback is here for fixtures and for a payload
  // shape that predates the server-side resolution above.
  return detail?.factsheetId;
};

/**
 * The factsheet a **connection-gated** signal may read: the same document, but
 * only while this adviser's relationship is live (or the client is 未開戶, which
 * is entirely adviser-owned).
 *
 * This is NOT a general access rule — it is the qualification V1 writes out by
 * hand in the two list states that need it, and nowhere else:
 *
 *  - 提醒我 (`isClientRemindMe`, utils/adviser-client.js): `adviserDetail.connection`
 *    before reading `fsReminderActive`/`fsReminderDate`.
 *  - 很久沒見面 (`clientPage.js:1775-1787`): `x.email === user.email && x.connection`
 *    before reading `latestMeetingDate`.
 *
 * V1 also blanks the last-meeting readout itself for a disconnected client
 * (`getClientDOB`/`getLastMeetingDate`, components/ClientEntry/utils.js:96-105).
 *
 * Everything else — identity, tier, tags, families, DISC, dob, msgOpt — reads
 * `adviserFactsheetOf`.
 */
export const qualifyingFactsheetOf = (client: QueueClient, adviserEmail?: string): ClientFactsheet | undefined => {
  const details = client.advisorDetails || [];
  if (details.length > 0) {
    const detail = details.find(entry => !adviserEmail || entry.email === adviserEmail);
    if (!detail?.connection) {
      return undefined;
    }
    return client.factsheetId || detail.factsheetId;
  }

  return client.inactiveUser ? client.factsheetId : undefined;
};
